"use strict";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const ViewportSize = {
    small: 'small',
    medium: 'medium',
    large: 'large',
}

class StoryPage {
    constructor() {
        this._scrollTimelines = [];

        this._viewportSize = this._identifyViewportSize();
    }

    initialize() {
        window.onload = () => this._handleWindowLoad();
        // Debounced: a raw resize event fires many times during a window drag
        // or a mobile address-bar show/hide, and each tick re-measures every
        // review card's scrollHeight and can rebuild the ScrollTrigger
        // timelines. Settling once after the resize burst ends produces the
        // identical final state without repeating that work on every
        // intermediate tick.
        window.onresize = typeof debounce === "function"
            ? debounce(() => this._handleWindowResize(), 150)
            : () => this._handleWindowResize();
    }

    _identifyViewportSize() {
        const windowWidth = window.innerWidth;
        let viewportSize;

        if (windowWidth < 750) {
            viewportSize = ViewportSize.small;
        } else if (windowWidth < 990) {
            viewportSize = ViewportSize.medium;
        } else {
            viewportSize = ViewportSize.large;
        }

        return viewportSize;
    }

    _handleWindowLoad() {
        // Lenis
        const lenis = new Lenis()
        function raf(time) {
            lenis.raf(time);
            requestAnimationFrame(raf);
        }
        requestAnimationFrame(raf);

        this._initializeJudgeMeReviewModalScroll();

        // Remove loader
        document.querySelector("#reviewsPage").classList.add("loaded");

        // Initial animation
        gsap.timeline()
            .fromTo('.heroSection', { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5 }, "+=0.5");

        // Initialize reviewCard
        this._initializeReviewCards();

        // Initialize scroll ani,ations
        this._initializeScrollTimelines();

    }

    _handleWindowResize() {
        // Re-initialize reviewCard
        this._initializeReviewCards();

        // Reload timelines if window size type changes
        const newViewportSize = this._identifyViewportSize();
        if (newViewportSize != this._viewportSize) {
            this._viewportSize = newViewportSize;
            this._initializeScrollTimelines();
        }
    }

    _initializeReviewCards() {
        const reviewCards = document.querySelectorAll(".reviewCard");

        // First pass: batch every layout-dependent read (scrollHeight) before
        // any write below. Reading scrollHeight for card N right after
        // writing style.height for card N-1 in the same loop forces a
        // synchronous layout recalculation on every iteration; reading all
        // cards first lets the browser compute layout once instead of once
        // per card.
        const measurements = [];
        for (const reviewCardEl of reviewCards) {
            const reviewTextEl = reviewCardEl.querySelector(".reviewText");
            const textEl = reviewTextEl.querySelector(".text");
            const fullTextEl = reviewTextEl.querySelector(".fullText");
            const isExpanded = reviewCardEl.classList.contains("expanded");
            const reviewTextElHeight = isExpanded
                ? fullTextEl.scrollHeight
                : textEl.scrollHeight;
            measurements.push({ reviewCardEl, reviewTextEl, reviewTextElHeight });
        }

        // Second pass: apply the writes and (re)attach the click listener --
        // no further layout reads occur below this line, so none of these
        // writes can trigger a forced reflow.
        for (const { reviewCardEl, reviewTextEl, reviewTextElHeight } of measurements) {
            reviewTextEl.style.height = `${reviewTextElHeight}px`;

            const expandButtonEl = reviewCardEl.querySelector(".reviewExpandButton");
            if (expandButtonEl) {
                reviewCardEl.removeEventListener("click", this._handleReviewExpandButtonClick)
                reviewCardEl.addEventListener("click", this._handleReviewExpandButtonClick);
            }
        }
    }

    _handleReviewExpandButtonClick(event) {
        const reviewCardEl = event.currentTarget;
        const reviewTextEl = reviewCardEl.querySelector(".reviewText");
        const isExpanded = reviewCardEl.classList.contains("expanded");
        if (isExpanded) {
            const textEl = reviewTextEl.querySelector(".text");
            const reviewTextElHeight = textEl.scrollHeight;
            reviewTextEl.style.height = `${reviewTextElHeight}px`;
            const onTransitioned = function () {
                reviewCardEl.classList.remove("expanded");
                reviewTextEl.removeEventListener("transitionend", onTransitioned);
            }
            reviewTextEl.addEventListener("transitionend", onTransitioned, false);
        } else {
            reviewCardEl.classList.add("expanded");
            const fullTextEl = reviewTextEl.querySelector(".fullText");
            const reviewTextElHeight = fullTextEl.scrollHeight;
            reviewTextEl.style.height = `${reviewTextElHeight}px`;
        }
    }

    _initializeJudgeMeReviewModalScroll() {
        const judgeMeModalSelectors = [
            ".jdgm-revs-tab",
            ".jdgm-revs-tab__content",
            ".jdgm-revs-tab__revs",
            ".jdgm-revs-tab__body",
            ".jdgm-modal",
            ".jdgm-modal__content",
            ".jdgm-all-reviews-widget",
        ].join(",");

        const markJudgeMeScrollContainers = () => {
            document.querySelectorAll(judgeMeModalSelectors).forEach((element) => {
                element.setAttribute("data-lenis-prevent", "");
                element.setAttribute("data-lenis-prevent-wheel", "");
                element.setAttribute("data-lenis-prevent-touch", "");
            });
        };

        const isInsideJudgeMeModal = (target) => {
            return target instanceof Element && target.closest(judgeMeModalSelectors);
        };

        ["wheel", "touchmove"].forEach((eventName) => {
            document.addEventListener(eventName, (event) => {
                if (!isInsideJudgeMeModal(event.target)) return;

                event.stopPropagation();
            }, { capture: true, passive: true });
        });

        markJudgeMeScrollContainers();

        // Debounced: this observer watches the whole document body (Judge.me's
        // tab/modal DOM can be inserted at any time), and every GSAP/
        // ScrollTrigger-driven DOM change on this page would otherwise
        // re-trigger a full-document querySelectorAll on every mutation
        // batch. Judge.me elements only need these attributes applied once
        // they exist and before the visitor actually scrolls/touches inside
        // them, so settling ~150ms after mutations stop is unnoticeable and
        // produces the same end state.
        const debouncedMarkJudgeMeScrollContainers = typeof debounce === "function"
            ? debounce(markJudgeMeScrollContainers, 150)
            : markJudgeMeScrollContainers;

        const judgeMeObserver = new MutationObserver(debouncedMarkJudgeMeScrollContainers);
        judgeMeObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    _initializeScrollTimelines() {

        // Kill any existing timelines
        let scrollTriggers = ScrollTrigger.getAll()
        for (const scrollTrigger of scrollTriggers) {
            scrollTrigger.kill(true)
        }
        for (const timeline of this._scrollTimelines) {
            timeline.kill(true);
        }

        // Clear up any previous timelines
        this._scrollTimelines = [];

        // Intilaize animations
        if (this._viewportSize === ViewportSize.small) {
            this._setupScrollTimelinesForSmallViewport();
        } else if (this._viewportSize === ViewportSize.medium) {
            this._setupScrollTimelinesForMediumViewport();
        } else {
            this._setupScrollTimelinesForLargeViewport();
        }

        // Preplay timelines
        this._prePlayTimelines();
    }

    _setupScrollTimelinesForSmallViewport() {
        // 
    }

    _setupScrollTimelinesForMediumViewport() {
        // 
    }

    _setupScrollTimelinesForLargeViewport() {
        const reviewsTimeline = gsap.timeline({
            scrollTrigger: {
                id: `reviewsSectionTimeline`,
                trigger: ".reviewsSection",
                start: 'top 100%',
                end: 'top 0%',
                scrub: 0,
                markers: false,
            }
        })
            .add(
                gsap.timeline()
                    .fromTo(
                        '.heroSection .titles .smallTitle, .heroSection .titles .title',
                        { backgroundPosition: '0%' },
                        { backgroundPosition: '100%', ease: "linear" },
                    )
                    .fromTo(
                        '.heroSection .titles .smallTitle, .heroSection .titles .title',
                        { backgroundPosition: '100%' },
                        { backgroundPosition: '0%', ease: "linear" },
                    ),
                "<"
            )
            .fromTo(
                ".heroSection .titles",
                { scale: 1 },
                { scale: 0.86, duration: 1 },
            )
            .fromTo(
                ".heroSection .arrows",
                { opacity: 1 },
                { opacity: 0, duration: 1 },
                "<"
            )
            .fromTo(
                "#reviewsColumn2",
                { y: "-200%" },
                { y: "0%", ease: "power1.out", duration: 2 },
                "<"
            );
        this._scrollTimelines.push(reviewsTimeline);
    }

    _prePlayTimelines() {
        // Preplay animations to ensure smoothness on animatiing during
        // window scroll. Note that we play animations in reverse order.
        for (let index = this._scrollTimelines.length - 1; index >= 0; index--) {
            const timeline = this._scrollTimelines[index];
            timeline.progress(1).progress(0);
        }
    }
}

const storyPage = new StoryPage();
storyPage.initialize();
